import { memo, useState, useCallback } from "react";
import { Smile, X, Search, Loader2 } from "lucide-react";

// Using Tenor's free tier (no API key required for basic usage)
const TENOR_API_KEY = "AIzaSyAyimkuYQYF_FXVALexPuGQctUWRURdCYQ"; // Google's public Tenor key

interface GifPickerProps {
  onSelectGif: (gifUrl: string) => void;
}

interface TenorGif {
  id: string;
  media_formats: {
    gif: { url: string };
    tinygif: { url: string };
  };
}

const TRENDING_TERMS = ["love", "happy", "excited", "wow", "heart", "kiss", "flirt"];

const GifPicker = memo(({ onSelectGif }: GifPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [gifs, setGifs] = useState<TenorGif[]>([]);
  const [loading, setLoading] = useState(false);

  const searchGifs = useCallback(async (query: string) => {
    if (!query.trim()) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `https://tenor.googleapis.com/v2/search?q=${encodeURIComponent(query)}&key=${TENOR_API_KEY}&limit=20&media_filter=gif,tinygif`
      );
      const data = await response.json();
      setGifs(data.results || []);
    } catch (error) {
      console.error("Error fetching GIFs:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTrending = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://tenor.googleapis.com/v2/featured?key=${TENOR_API_KEY}&limit=20&media_filter=gif,tinygif`
      );
      const data = await response.json();
      setGifs(data.results || []);
    } catch (error) {
      console.error("Error fetching trending GIFs:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    loadTrending();
  }, [loadTrending]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
    setGifs([]);
  }, []);

  const handleSelectGif = useCallback((gif: TenorGif) => {
    const gifUrl = gif.media_formats.gif?.url || gif.media_formats.tinygif?.url;
    if (gifUrl) {
      onSelectGif(gifUrl);
      handleClose();
    }
  }, [onSelectGif, handleClose]);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    searchGifs(searchQuery);
  }, [searchQuery, searchGifs]);

  if (!isOpen) {
    return (
      <button
        onClick={handleOpen}
        className="p-2 rounded-full hover:bg-muted transition-colors text-xs font-bold"
        title="Send GIF"
      >
        GIF
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center sm:items-center">
      <div className="bg-card w-full max-w-md max-h-[70vh] rounded-t-2xl sm:rounded-2xl flex flex-col animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-semibold">Choose a GIF</h3>
          <button onClick={handleClose} className="p-1 hover:bg-muted rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="p-3 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search GIFs..."
              className="w-full pl-10 pr-4 py-2 rounded-full bg-muted text-sm focus:ring-2 focus:ring-pop outline-none"
            />
          </div>
        </form>

        {/* Quick searches */}
        <div className="flex gap-2 p-3 overflow-x-auto">
          {TRENDING_TERMS.map((term) => (
            <button
              key={term}
              onClick={() => {
                setSearchQuery(term);
                searchGifs(term);
              }}
              className="px-3 py-1 rounded-full bg-muted text-sm whitespace-nowrap hover:bg-pop hover:text-white transition-colors"
            >
              {term}
            </button>
          ))}
        </div>

        {/* GIF Grid */}
        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-pop" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {gifs.map((gif) => (
                <button
                  key={gif.id}
                  onClick={() => handleSelectGif(gif)}
                  className="aspect-square overflow-hidden rounded-lg hover:ring-2 hover:ring-pop transition-all"
                >
                  <img
                    src={gif.media_formats.tinygif?.url || gif.media_formats.gif?.url}
                    alt="GIF"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Powered by Tenor */}
        <div className="p-2 text-center text-xs text-muted-foreground border-t border-border">
          Powered by Tenor
        </div>
      </div>
    </div>
  );
});

GifPicker.displayName = "GifPicker";

export default GifPicker;
